/**
 * 프로젝트 전역 상수 — 모든 콘텐츠는 이 파일에서 관리
 *
 * 원칙:
 * - 텍스트, 서비스 정보, 혜택, FAQ 등 모든 콘텐츠는 이 파일에서 수정
 * - 컴포넌트는 이 파일을 import해서 사용만 함
 * - 브랜드/회사 정보 변경 시 이 파일만 수정하면 전체 반영
 */

// ================================================================
// 회사 정보
//
// 구조:
// - 전면 브랜드(고객 노출): 인증이지 / CERT EASY
// - 법적 공급자(푸터·개인정보처리방침): (주)메이커머스
// ================================================================
export const COMPANY = {
  nameKo: '인증이지',
  nameEn: 'CERT EASY',
  brandKo: '인증이지',
  brandEn: 'CERT EASY',

  legalNameKo: '주식회사 메이커머스',
  legalNameEn: 'Makermas',
  ceo: '김형철',
  businessNumber: '748-88-01787',
  address: '서울특별시 강서구 마곡중앙1로 10, 8층 803호',
  addressDetail: '(마곡동, 한일노벨리아빌딩)',
  tagline: '기업 성장 원스톱 파트너',
  domain: 'certifyeasy.kr',

  contactEmail: 'tech@taxcube.kr',
} as const;

// ================================================================
// 서비스 5개
// ----------------------------------------------------------------
// - SERVICE_TYPES: 순회 시 사용 (배열, 시안 표시 순서와 일치)
// - SERVICES: id로 메타 직접 조회 (Record<ServiceType, ServiceMeta>)
//   → 어드민에서 SERVICES['venture'] 형태로 접근하므로 객체 유지 필수
// - 랜딩에서 순회할 때: SERVICE_TYPES.map(id => SERVICES[id])
// ================================================================
export const SERVICE_TYPES = [
  'consulting',
  'venture',
  'research',
  'iso',
  'bookkeeping',
] as const;

export type ServiceType = (typeof SERVICE_TYPES)[number];

export interface ServiceMeta {
  id: ServiceType;
  /** 한국어 라벨 (Hero 탭/Services 카드/Contact 폼) */
  label: string;
  /** 영문 라벨 (서비스 카드 상단 라벨) */
  labelEn: string;
  /** Hero 이미지 경로 (탭 스왑용) */
  heroImg: string;
  /** Services 카드 본문 */
  desc: string;
  /** Services 카드 하단 회색 주석 (※) */
  note: string;
}

export const SERVICES: Record<ServiceType, ServiceMeta> = {
  consulting: {
    id: 'consulting',
    label: '경영컨설팅',
    labelEn: 'CONSULTING',
    heroImg: '/hero-consulting.webp',
    desc: '정책자금 신청 절차 안내, 사업계획서 작성 지원, 경영 현황 분석 등의 컨설팅 서비스를 제공합니다.',
    note: '※ 법무·세무 업무는 협력 전문가(변호사·세무사)를 통해 진행됩니다.',
  },
  venture: {
    id: 'venture',
    label: '벤처인증',
    labelEn: 'VENTURE',
    heroImg: '/hero-venture.webp',
    desc: '벤처기업확인 신청에 필요한 컨설팅 서비스를 제공합니다. 확인 취득 시 법인세 감면·정책자금 우대·병역특례 신청 자격 등의 혜택을 받으실 수 있습니다.',
    note: '※ 혜택은 관계 법령 및 심사 결과에 따라 달라질 수 있습니다.',
  },
  research: {
    id: 'research',
    label: '기업부설연구소',
    labelEn: 'R&D LAB',
    heroImg: '/hero-rnd.webp',
    desc: '기업부설연구소 설립 준비 및 인정 신청에 필요한 컨설팅 서비스를 제공합니다. 인정 취득 시 R&D 세액공제, 정부 R&D 과제 신청, 전문연구요원 활용 등이 가능해집니다.',
    note: '※ 적용 여부는 요건 및 관계기관 심사에 따릅니다.',
  },
  iso: {
    id: 'iso',
    label: 'ISO인증',
    labelEn: 'ISO',
    heroImg: '/hero-iso.webp',
    desc: 'ISO 9001·14001·45001 등 국제표준 인증 취득을 지원합니다. 인증 보유 시 수출·거래처 신뢰도 향상에 도움이 될 수 있으며, 일부 공공조달 입찰에서 가점 요소로 활용될 수 있습니다.',
    note: '※ 가점 여부는 발주처 기준에 따라 다릅니다.',
  },
  bookkeeping: {
    id: 'bookkeeping',
    label: '경리아웃소싱',
    labelEn: 'OPERATIONS',
    heroImg: '/hero-ops.webp',
    desc: '급여 처리, 세무 일정 관리, 증빙 서류 정리, 경리 보고 업무 등의 컨설팅 서비스를 제공합니다.',
    note: '※ 세무 신고·세금 계산은 당사와 협력하는 등록 세무사를 통해 진행됩니다.',
  },
};

/** 랜딩 표시 순서대로 ServiceMeta 배열 — 컴포넌트 내 .map() 편의용 */
export const SERVICE_LIST: ServiceMeta[] = SERVICE_TYPES.map((id) => SERVICES[id]);

/** Hero 우상단 통계 박스 텍스트 */
export const HERO_STAT = {
  title: '누적상담 1,200건+',
  subtitle: '5개 분야 전문 컨설턴트 상주',
} as const;

// ================================================================
// WhyUs 4개 카드
// ================================================================
export interface WhyUsCard {
  num: string;
  numUnit?: string;
  title: string;
  desc: string;
}
export const WHYUS_CARDS: WhyUsCard[] = [
  { num: '5개 분야', title: '통합 컨설팅 운영', desc: '경영·벤처·연구소·ISO·경리' },
  { num: '전담 매칭', title: '분야별 컨설턴트 배정', desc: '회사 상황별 맞춤 안내' },
  { num: '책임 진행', title: '서류 준비부터 심사 신청까지', desc: '단계별 일정·진행 안내' },
  { num: '24', numUnit: 'h', title: '평균 초기 응답', desc: '※ 영업일 기준' },
];

// ================================================================
// Benefits 10개 (슬라이드, 5개씩 2페이지)
// ================================================================
export interface BenefitCard {
  num: string;
  title: string;
  tag: string;
  desc: string;
  note?: string;
  /** SVG path 마크업 (viewBox 0 0 24 24 기준) */
  iconSvg: string;
}
export const BENEFITS: BenefitCard[] = [
  {
    num: '01',
    title: '법인세 감면',
    tag: '최대 50% 감면 가능',
    desc: '벤처인증 확인 시 일정 기간 법인세 감면 혜택이 적용될 수 있습니다.',
    note: '※ 기업 규모·과세표준·적용 요건에 따라 상이, 세무사 확인 필요',
    iconSvg: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
  },
  {
    num: '02',
    title: '취득세 감면',
    tag: '최대 75% 감면 가능',
    desc: '사업용 부동산 취득 시 지방세특례제한법에 따른 감면이 적용될 수 있습니다.',
    note: '※ 지방세특례제한법 기준, 지자체·취득 유형·시기에 따라 상이하며 실제 감면율은 관할 세무서 확인 필요',
    iconSvg: '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
  },
  {
    num: '03',
    title: 'R&D 세액공제',
    tag: '최대 25% 공제',
    desc: '기업부설연구소 보유 기업의 연구·인력개발비 세액공제 (중소기업 기준 최대치).<br/>예: R&D 2억원 지출 시 최대 5,000만원',
    note: '※ 기업 규모·공제 유형에 따라 상이',
    iconSvg: '<path d="M10 2v7.31"/><path d="M14 9.3V1.99"/><path d="M8.5 2h7"/><path d="M14 9.3a6.5 6.5 0 1 1-4 0"/>',
  },
  {
    num: '04',
    title: '병역특례 배정',
    tag: '신청 자격 확보',
    desc: '전문연구요원·산업기능요원 배정 신청 자격 확보가 가능합니다.',
    note: '※ 신청 및 배정 여부는 기업 요건, 병무청 심사, 해당 연도 배정 방침에 따라 달라질 수 있습니다.',
    iconSvg: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  },
  {
    num: '05',
    title: '정부 과제 참여',
    tag: '신청 요건 충족',
    desc: '정부 R&D 과제 신청 요건 충족이 가능해집니다.',
    note: '※ 실제 신청 및 선정은 관계기관 심사에 따릅니다.',
    iconSvg: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/>',
  },
  {
    num: '06',
    title: '기술보호 지원',
    tag: '최대 70% 감면',
    desc: '특허 출원 수수료 감면, 기술임치 등 IP 보호 지원이 가능합니다.',
    note: '※ 감면율은 기업 규모·출원 유형에 따라 상이',
    iconSvg: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
  },
  {
    num: '07',
    title: 'ISO 국제표준 인증',
    tag: '글로벌 거래 우대',
    desc: '글로벌 거래·수출 시 인증 요건 충족에 도움.<br/>ISO 9001/14001/45001/27001 등.',
    note: '※ 거래처 요구 기준에 따라 달라질 수 있습니다.',
    iconSvg: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  },
  {
    num: '08',
    title: '공공입찰 가산점',
    tag: '최대 2점 가산',
    desc: '조달청 입찰 시 ISO 인증으로 가산점이 적용될 수 있습니다.',
    note: '※ 조달청 평가사 기준, 입찰 유형 및 발주처에 따라 상이하며 변경될 수 있음',
    iconSvg: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
  },
  {
    num: '09',
    title: '경리 인건비 절감',
    tag: '비용 절감 가능',
    desc: '경리아웃소싱을 통해 인건비 절감이 가능합니다.',
    note: '※ 경리직원 1인 채용 대비 기준, 기업 규모·업무 범위에 따라 상이',
    iconSvg: '<line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/>',
  },
  {
    num: '10',
    title: '체계적 경리 운영',
    tag: '전문가 연계',
    desc: '세무·신고 자격이 필요한 업무는 협력 회계법인을 통해 진행됩니다.',
    note: '※ 인증이지는 경리 운영 흐름의 정리 및 일정 관리를 지원합니다.',
    iconSvg: '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>',
  },
];

// ================================================================
// Process 4단계
// ================================================================
export interface ProcessStep {
  num: number;
  title: string;
  desc: string;
  duration: string;
}
export const PROCESS_STEPS: ProcessStep[] = [
  { num: 1, title: '신청 및 서류 업로드', desc: '유형 선택 후 신청서와 제출서류를 온라인으로 접수합니다.', duration: '약 7일 내외' },
  { num: 2, title: '서류 확인 및 접수완료', desc: '제출서류 확인 및 보완 후 수수료 납부가 진행됩니다.', duration: '접수까지 약 28일 이내' },
  { num: 3, title: '전문평가기관 평가', desc: '유형에 따라 서류 검토, 요건 검토, 현장 실제조사가 진행됩니다.', duration: '약 14일 내외' },
  { num: 4, title: '심의 및 확인서 발급', desc: '확인위원회 심의 후 결과가 안내되며, 확인서를 발급받습니다.', duration: '심의 후 발급' },
];

export const PROCESS_NOTICE =
  '※ 벤처기업확인 결과 안내 기간은 접수완료일 기준 벤처투자유형 30일, 연구개발유형·혁신성장유형 45일 내외이며, 서류 보완 및 평가 상황에 따라 달라질 수 있습니다.';

// ================================================================
// Compare 표
// ================================================================
export interface CompareRow {
  label: string;
  before: string;
  /** after는 HTML 가능 (<strong>) */
  after: string;
}
export const COMPARE_ROWS: CompareRow[] = [
  { label: '법인세', before: '일반 세율 적용', after: '요건 충족 시 <strong>최대 50% 감면</strong> 가능' },
  { label: '취득세', before: '일반 세율 적용', after: '요건 충족 시 <strong>최대 75% 감면</strong> 가능' },
  { label: 'R&D 세액공제', before: '일반 공제율 적용', after: '요건 충족 시 <strong>최대 25% 공제</strong> 가능' },
  { label: '정책자금', before: '일반 요건 심사', after: '사업별 기준에 따라 신청 시 <strong>우대 가능</strong>' },
  { label: '병역특례', before: '신청 자격 제한', after: '요건 충족 시 <strong>전문연구요원 신청 자격</strong> 검토 가능' },
  { label: 'ISO 국제인증', before: '인증 요건 별도 확인 필요', after: '<strong>거래 신뢰도 확보</strong> 및 입찰 우대 가능성 검토' },
  { label: '경리 운영', before: '내부 채용·관리 부담', after: '<strong>경리 업무 체계화</strong> 및 필요 시 전문가 연계' },
];

export const COMPARE_NOTICE =
  '※ 상기 내용은 일반적인 제도 안내이며, 세제 혜택·정책자금·병역특례·R&D 세액공제·입찰 우대 등은 기업 요건, 관계 법령, 심사 기준 및 공고 내용에 따라 적용 여부가 달라질 수 있습니다.';

// ================================================================
// HowWeHelp 5탭
// ================================================================
export interface HowHelpPanel {
  id: string;
  label: string;
  sub: string;
  title: string;
  desc: string;
  note: string;
}
export const HOWHELP_PANELS: HowHelpPanel[] = [
  { id: 'venture', label: '벤처인증', sub: 'VENTURE', title: '기술평가 기반 진행',
    desc: '기술성 검토부터 벤처기업 확인 신청까지 단계별로 컨설팅합니다.',
    note: '기업 현황과 기술 자료를 정리해 평가 준비를 지원합니다.' },
  { id: 'rnd', label: '기업부설연구소', sub: 'R&D LAB', title: '연구소 인정 절차',
    desc: '연구전담부서 조직 설계부터 KOITA 신청까지 단계별로 컨설팅합니다.',
    note: '인정 요건, 인력, 공간, 서류 정리를 지원합니다.' },
  { id: 'consulting', label: '경영컨설팅', sub: 'CONSULTING', title: '정책자금 신청 자료',
    desc: '사업계획서 작성, 재무 현황 검토, 신청 자료 구성을 컨설팅합니다.',
    note: '기업 상황에 맞춘 자료 구성과 신청 절차를 안내합니다.' },
  { id: 'iso', label: 'ISO인증', sub: 'ISO', title: 'ISO 9001 인증 절차',
    desc: '품질경영시스템 매뉴얼 구축부터 심사 준비까지 단계별로 컨설팅합니다.',
    note: '심사 준비 자료 정리와 인증 이후 관리 방향을 안내합니다.' },
  { id: 'ops', label: '경리 운영 지원', sub: 'OPERATIONS', title: '경리 운영 체계화',
    desc: '급여, 4대보험, 증빙 정리, 세무 일정 관리까지 운영 흐름을 정리합니다.',
    note: '필요 시 제휴 전문가 연계와 운영 일정 정착을 지원합니다.' },
];

export const HOWHELP_NOTICES: string[] = [
  '※ 상기 내용은 서비스 진행 흐름에 대한 일반 안내이며, 실제 절차는 기업 상황, 심사 기준, 관계 법령 및 기관별 기준에 따라 달라질 수 있습니다.',
  '※ 세무 신고, 회계 감사, 법률 자문 등 자격이 필요한 업무는 관련 전문가 또는 제휴 기관을 통해 진행될 수 있습니다.',
];

// ================================================================
// FAQ 9개
// ================================================================
export interface FaqItem {
  q: string;
  a: string;
}
export const FAQS: FaqItem[] = [
  { q: '어떤 서비스를 먼저 받아야 하나요?',
    a: '기업 상황에 따라 다릅니다. 상담 후 현재 상태를 분석해 적합한 진행 순서를 안내드립니다. 보통 벤처인증이나 연구소 설립을 먼저 검토하는 경우가 많습니다.' },
  { q: '여러 서비스를 동시에 진행할 수 있나요?',
    a: '네, 병행 진행 가능합니다. 동시에 진행하면 서류와 일정 관리가 효율적일 수 있으며, 전담 컨설턴트가 전체 일정을 조율합니다.' },
  { q: '비용은 어떻게 되나요?',
    a: '초기 상담은 무료입니다. 후속 컨설팅 비용은 서비스 종류와 기업 상황에 따라 개별 안내드립니다.' },
  { q: '설립 초기 기업도 신청 가능한가요?',
    a: '네, 가능합니다. 다만 제도별 요건이 다르므로 상담을 통해 신청 가능 여부와 준비 방향을 먼저 확인해드립니다.' },
  { q: '인증 후 사후관리는요?',
    a: '각 인증의 유효기간과 갱신 시점에 맞춰 사전 안내를 드립니다. 벤처기업확인은 일반적으로 3년, ISO 인증은 일반적으로 3년 유효하며 사후심사 일정 관리가 필요합니다.' },
  { q: 'ISO인증은 어떤 종류가 있나요?',
    a: '대표적으로 ISO 9001(품질경영), ISO 14001(환경경영), ISO 45001(안전보건경영), ISO 27001(정보보안) 등이 있습니다. 수출 기업, 제조업체, SI업체 등 업종과 거래처 요구에 맞는 인증을 안내드립니다.' },
  { q: 'ISO인증 취득까지 얼마나 걸리나요?',
    a: '기업 규모와 기존 시스템 수준에 따라 다르지만, 일반적으로 매뉴얼 구축부터 인증 심사까지 약 1~3개월이 소요될 수 있습니다. 긴급 일정은 가능 여부를 별도 검토합니다.' },
  { q: '경리 운영 지원은 어떤 업무를 도와주시나요?',
    a: '경리 운영 전반의 체계화를 지원합니다. 급여 처리, 4대보험 신고 일정, 세무 일정 관리, 증빙 정리, 자금 일정 관리, 월별 리포트 등을 기업 상황에 맞춰 지원합니다. 세무 신고·회계 감사 등 자격이 필요한 업무는 제휴 세무사무소·회계법인과 연계해 진행됩니다.' },
  { q: '경리직원이 이미 고용 중인데 운영 지원을 받을 수 있나요?',
    a: '네, 가능합니다. 기존 경리직원의 업무를 보완하거나, 운영 체계를 함께 정리하는 방식으로 지원할 수 있습니다. 기업 상황에 맞춰 업무 범위를 조율해드립니다.' },
];

// ================================================================
// B2B 모달 접촉 경로 옵션
// ================================================================
export const B2B_SOURCE_OPTIONS = [
  '전화 영업',
  '방문 영업',
  '이메일',
  '지인 소개',
  '전시회 / 박람회',
  '기타',
] as const;

// ================================================================
// 채널 (인바운드: 일반 상담 폼, 아웃바운드: B2B 모달)
// schemas/inquiry.ts 와 types/inquiry.ts 에서 참조
// ================================================================
export const CHANNELS = ['inbound', 'outbound'] as const;
export type Channel = (typeof CHANNELS)[number];

// ================================================================
// 접수 상태 (어드민에서 단계별 관리)
// ================================================================
export const INQUIRY_STATUSES = [
  'pending',
  'contacted',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type InquiryStatus = (typeof INQUIRY_STATUSES)[number];

/**
 * 접수 상태 한글 라벨 — 어드민 UI에서 import해서 사용
 * (어드민 AdminInquiryDetail / AdminHome / AdminInquiries 가 참조)
 */
export const INQUIRY_STATUS_LABEL: Record<InquiryStatus, string> = {
  pending: '대기',
  contacted: '상담중',
  in_progress: '진행중',
  completed: '완료',
  cancelled: '취소',
};

// ================================================================
// 디버그 플래그 — api/client.ts 의 console.log 게이트
// ================================================================
export const IS_DEBUG = import.meta.env.VITE_DEBUG === 'true';
